/**
 * ProposalCard — inline approval card for a Co-Ontologist graph mutation proposal.
 *
 * Renders differently per proposal kind:
 *   add_node     — shows proposed label + description + rationale
 *   add_relation — shows source → relation type → target + rationale
 *   edit_node    — shows current vs proposed field values side-by-side
 *
 * Accept/Reject buttons are disabled after a decision is made.
 * The card shows an outcome indicator (accepted / rejected) once settled.
 *
 * No modals, no lists — one card per proposal, rendered inline in the chat.
 */

import { Button, Tag } from '@carbon/react';
import { CheckmarkFilled, CloseFilled, ArrowRight } from '@carbon/icons-react';
import type { GraphProposal, AddNodeProposal, AddRelationProposal, EditNodeProposal } from '@snxt/snxt-onto-share';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ProposalDecision = 'accepted' | 'rejected' | null;

/** A near-match returned by the duplicate-check similarity search. */
export interface NearMatch {
  nodeId: string;
  label: string;
  similarity: number;
}

export interface ProposalCardProps {
  proposal: GraphProposal;
  /** Decision already made externally (lifted state). Null = pending. */
  decision: ProposalDecision;
  onAccept: (proposal: GraphProposal) => void;
  onReject: (proposal: GraphProposal) => void;
  /**
   * When set, the Accept button is disabled and this message is shown as the reason.
   * Used to enforce ordering: an add_relation can't be accepted before its add_node.
   */
  disabledReason?: string;
  /**
   * Near-match nodes found by vector similarity search when the user hit Accept.
   * When non-empty the card shows a duplicate-warning banner requiring explicit
   * confirmation before the mutation is applied.
   */
  nearMatches?: NearMatch[];
  /** Called when the user confirms they want to add despite a near-match warning. */
  onConfirmDespiteDuplicate?: (proposal: GraphProposal) => void;
}

// ─── Shared style tokens ──────────────────────────────────────────────────────

const S = {
  card: (decision: ProposalDecision): React.CSSProperties => ({
    border: `1px solid ${decision === 'accepted' ? 'var(--cds-support-success)' : decision === 'rejected' ? 'var(--cds-border-subtle-01)' : 'var(--cds-interactive-01)'}`,
    borderRadius: '6px',
    padding: '0.625rem 0.75rem',
    background: decision === 'accepted'
      ? 'var(--cds-support-success-inverse, #defbe6)'
      : decision === 'rejected'
      ? 'var(--cds-layer-01)'
      : 'var(--cds-layer-02)',
    opacity: decision === 'rejected' ? 0.6 : 1,
    fontSize: '0.8125rem',
    lineHeight: 1.5,
  }),
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.375rem',
  } as React.CSSProperties,
  title: {
    fontWeight: 600,
    fontSize: '0.8125rem',
    color: 'var(--cds-text-secondary)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  outcome: (decision: 'accepted' | 'rejected'): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: decision === 'accepted' ? 'var(--cds-support-success)' : 'var(--cds-text-secondary)',
  }),
  row: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.125rem',
    marginBottom: '0.25rem',
  },
  fieldLabel: {
    fontSize: '0.6875rem',
    color: 'var(--cds-text-secondary)',
    fontWeight: 500,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  },
  value: {
    fontSize: '0.875rem',
    color: 'var(--cds-text-primary)',
    fontWeight: 500,
  },
  valueProposed: {
    fontSize: '0.875rem',
    color: 'var(--cds-text-primary)',
    fontStyle: 'italic' as const,
  },
  editDiff: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem',
    fontSize: '0.875rem',
  },
  editBefore: {
    color: 'var(--cds-text-secondary)',
    textDecoration: 'line-through' as const,
  },
  editAfter: {
    color: 'var(--cds-text-primary)',
    fontWeight: 500,
  },
  relationRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    flexWrap: 'wrap' as const,
    marginBottom: '0.25rem',
  },
  rationale: {
    marginTop: '0.375rem',
    fontSize: '0.75rem',
    color: 'var(--cds-text-secondary)',
    fontStyle: 'italic' as const,
    lineHeight: 1.5,
    borderTop: '1px solid var(--cds-border-subtle-01)',
    paddingTop: '0.375rem',
  },
  actions: {
    marginTop: '0.5rem',
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
  } as React.CSSProperties,
  duplicateWarning: {
    marginTop: '0.5rem',
    padding: '0.5rem 0.625rem',
    borderRadius: '4px',
    background: 'var(--cds-support-warning-inverse, #fff8e1)',
    border: '1px solid var(--cds-support-warning, #f1c21b)',
    fontSize: '0.75rem',
    lineHeight: 1.5,
  } as React.CSSProperties,
  duplicateWarningTitle: {
    fontWeight: 600,
    color: 'var(--cds-text-primary)',
    marginBottom: '0.25rem',
  } as React.CSSProperties,
  duplicateMatchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    margin: '0.125rem 0',
  } as React.CSSProperties,
  duplicateMatchLabel: {
    fontWeight: 500,
    color: 'var(--cds-text-primary)',
  } as React.CSSProperties,
  duplicateMatchScore: {
    color: 'var(--cds-text-secondary)',
    fontSize: '0.6875rem',
  } as React.CSSProperties,
  duplicateActions: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
    marginTop: '0.5rem',
  } as React.CSSProperties,
};

// ─── Sub-renderers ────────────────────────────────────────────────────────────

/** Renders a proposed properties list as compact key[=value] tags. */
function PropertiesRows({ properties }: { properties: Array<{ name: string; value?: string }> }) {
  if (properties.length === 0) return null;
  return (
    <div style={S.row}>
      <span style={S.fieldLabel}>Properties</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.125rem' }}>
        {properties.map((p) => (
          <span
            key={p.name}
            style={{
              fontSize: '0.6875rem',
              padding: '0.1rem 0.45rem',
              borderRadius: '0.75rem',
              background: 'var(--cds-layer-accent-01)',
              color: 'var(--cds-text-primary)',
              whiteSpace: 'nowrap',
            }}
          >
            {p.value ? `${p.name} = ${p.value}` : p.name}
          </span>
        ))}
      </div>
    </div>
  );
}

function AddNodeBody({ proposal }: { proposal: AddNodeProposal }) {
  return (
    <div>
      <div style={S.row}>
        <span style={S.fieldLabel}>New concept</span>
        <span style={S.value}>{proposal.label}</span>
      </div>
      {proposal.description && (
        <div style={S.row}>
          <span style={S.fieldLabel}>Description</span>
          <span style={{ ...S.value, fontWeight: 400 }}>{proposal.description}</span>
        </div>
      )}
      {proposal.properties && proposal.properties.length > 0 && (
        <PropertiesRows properties={proposal.properties} />
      )}
    </div>
  );
}

function AddRelationBody({ proposal }: { proposal: AddRelationProposal }) {
  return (
    <div style={S.relationRow}>
      <Tag type="blue" size="sm">{proposal.sourceNodeLabel}</Tag>
      <ArrowRight size={14} style={{ color: 'var(--cds-text-secondary)', flexShrink: 0 }} aria-hidden="true" />
      <Tag type="gray" size="sm">{proposal.label}</Tag>
      <ArrowRight size={14} style={{ color: 'var(--cds-text-secondary)', flexShrink: 0 }} aria-hidden="true" />
      <Tag type="blue" size="sm">{proposal.targetNodeLabel}</Tag>
    </div>
  );
}

function EditNodeBody({ proposal }: { proposal: EditNodeProposal }) {
  const hasLabelChange = !!proposal.label && proposal.label !== proposal.currentLabel;

  return (
    <div>
      {hasLabelChange ? (
        <div style={S.row}>
          <span style={S.fieldLabel}>Label</span>
          <span style={S.editDiff}>
            <span style={S.editBefore}>{proposal.currentLabel}</span>
            <ArrowRight size={12} aria-hidden="true" />
            <span style={S.editAfter}>{proposal.label}</span>
          </span>
        </div>
      ) : (
        <div style={S.row}>
          <span style={S.fieldLabel}>Node</span>
          <span style={S.value}>{proposal.currentLabel}</span>
        </div>
      )}
      {proposal.description !== undefined && (
        <div style={S.row}>
          <span style={S.fieldLabel}>New description</span>
          <span style={S.valueProposed}>{proposal.description}</span>
        </div>
      )}
      {proposal.properties && proposal.properties.length > 0 && (
        <PropertiesRows properties={proposal.properties} />
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function proposalTitle(proposal: GraphProposal): string {
  switch (proposal.kind) {
    case 'add_node':     return 'Add concept';
    case 'add_relation': return 'Add relation';
    case 'edit_node':    return 'Edit concept';
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ProposalCard({
  proposal,
  decision,
  onAccept,
  onReject,
  disabledReason,
  nearMatches,
  onConfirmDespiteDuplicate,
}: ProposalCardProps) {
  const handleAccept = () => {
    onAccept(proposal);
  };

  const handleReject = () => {
    onReject(proposal);
  };

  const rationale: string | undefined =
    (proposal as { rationale?: string }).rationale;

  const hasDuplicateWarning = (nearMatches?.length ?? 0) > 0;

  return (
    <div
      style={S.card(decision)}
      data-testid="proposal-card"
    >
      {/* Header */}
      <div style={S.header}>
        <span style={S.title}>{proposalTitle(proposal)}</span>
        {decision && (
          <span
            style={S.outcome(decision)}
            data-testid={`proposal-outcome-${decision}`}
          >
            {decision === 'accepted'
              ? <><CheckmarkFilled size={14} /> Accepted</>
              : <><CloseFilled size={14} /> Rejected</>
            }
          </span>
        )}
      </div>

      {/* Proposal-specific body */}
      {proposal.kind === 'add_node'     && <AddNodeBody proposal={proposal} />}
      {proposal.kind === 'add_relation' && <AddRelationBody proposal={proposal} />}
      {proposal.kind === 'edit_node'    && <EditNodeBody proposal={proposal} />}

      {/* Rationale (optional) */}
      {rationale && (
        <div style={S.rationale}>
          {rationale}
        </div>
      )}

      {/* Duplicate-concept warning — shown when similarity search found near-matches */}
      {hasDuplicateWarning && !decision && (
        <div style={S.duplicateWarning} data-testid="duplicate-warning">
          <div style={S.duplicateWarningTitle}>
            ⚠ Similar concept{nearMatches!.length > 1 ? 's' : ''} already exist{nearMatches!.length === 1 ? 's' : ''}
          </div>
          {nearMatches!.map((m) => (
            <div key={m.nodeId} style={S.duplicateMatchRow} data-testid="duplicate-match">
              <span style={S.duplicateMatchLabel}>{m.label}</span>
              <span style={S.duplicateMatchScore}>{Math.round(m.similarity * 100)}% similar</span>
            </div>
          ))}
          <div style={S.duplicateActions}>
            <Button
              kind="ghost"
              size="sm"
              onClick={handleReject}
              data-testid="duplicate-cancel"
            >
              Cancel
            </Button>
            <Button
              kind="danger--tertiary"
              size="sm"
              onClick={() => onConfirmDespiteDuplicate?.(proposal)}
              data-testid="duplicate-confirm"
            >
              Add anyway
            </Button>
          </div>
        </div>
      )}

      {/* Normal actions — hidden while the duplicate warning is shown */}
      {!decision && !hasDuplicateWarning && (
        <div style={{ ...S.actions, flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
          {disabledReason && (
            <span style={{
              fontSize: '0.6875rem',
              color: 'var(--cds-text-secondary)',
              fontStyle: 'italic',
            }}
              data-testid="proposal-disabled-reason"
            >
              ⚠ {disabledReason}
            </span>
          )}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              kind="ghost"
              size="sm"
              onClick={handleReject}
              disabled={!!disabledReason}
              data-testid="proposal-reject"
            >
              Reject
            </Button>
            <Button
              kind="primary"
              size="sm"
              onClick={handleAccept}
              disabled={!!disabledReason}
              data-testid="proposal-accept"
            >
              Accept
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
